import type {ReactElement} from 'react';
import {useState} from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import {AiChatChristmasAssetImage} from '@AiChat/components/ai-chat-christmas-asset-image/ai-chat-christmas-asset-image';
import {AiChatChristmasAsset} from '@AiChat/lib/ai-chat-season-library.types';
import {isAiChatMotionReduced} from '@AiChat/lib/ai-chat-motion.library';
import type {CssVariableStyle} from '@AiChat/lib/ai-chat-style.types';
import {assertNever} from '@Utils/assert-never.util';
import type {AiChatChristmasBulb, AiChatChristmasTreeSnowDot} from './ai-chat-christmas-tree.types';
import {AI_CHAT_CHRISTMAS_TREE_BULBS, AI_CHAT_CHRISTMAS_TREE_SNOW, AiChatChristmasBulbColor, STAR_SPARK_ANGLES_DEG} from './ai-chat-christmas-tree.types';
import styles from './ai-chat-christmas-tree.module.scss';

export function AiChatChristmasTree(): ReactElement {
  const [chaseCount, setChaseCount] = useState<number>(0);
  const [isChasing, setIsChasing] = useState<boolean>(false);

  const onTreeClick = (): void => {
    if (isAiChatMotionReduced()) {
      setIsChasing(!isChasing);
      return;
    }

    setChaseCount(chaseCount + 1);
    setIsChasing(true);
  };

  const getBulbAnimationEndHandler = (index: number): VoidFunction => {
    return (): void => {
      if (!isLastBulb(index)) {
        return;
      }

      setIsChasing(false);
    };
  };

  const isLastBulb = (index: number): boolean => {
    return index === AI_CHAT_CHRISTMAS_TREE_BULBS.length - 1;
  };

  const getTreeClassName = (): string => {
    if (isChasing) {
      return `${styles.tree} ${styles.isChasing}`;
    }

    return styles.tree;
  };

  const getBulb = (bulb: AiChatChristmasBulb, index: number): ReactElement => {
    return (
      <span
        key={index}
        className={`${styles.bulb} ${getBulbColorClassName(bulb.color)}`}
        style={getBulbStyle(bulb, index)}
        onAnimationEnd={getBulbAnimationEndHandler(index)}
      />
    );
  };

  const getBulbColorClassName = (color: AiChatChristmasBulbColor): string => {
    switch (color) {
      case AiChatChristmasBulbColor.WARM:
        return styles.warmBulb;
      case AiChatChristmasBulbColor.RED:
        return styles.redBulb;
      case AiChatChristmasBulbColor.BLUE:
        return styles.blueBulb;
      default:
        return assertNever(color);
    }
  };

  const getBulbStyle = (bulb: AiChatChristmasBulb, index: number): CssVariableStyle => {
    return {
      '--bulb-index': index,
      '--twinkle-delay': `-${bulb.twinkleOffsetSeconds}s`,
      '--twinkle-duration': `${bulb.twinkleSeconds}s`,
      left: `${bulb.leftPercent}%`,
      top: `${bulb.topPercent}%`,
    };
  };

  const getStarSpark = (angle: number): ReactElement => {
    return <span key={angle} className={styles.starSpark} style={getStarSparkStyle(angle)} />;
  };

  const getStarSparkStyle = (angle: number): CssVariableStyle => {
    return {'--spark-angle': `${angle}deg`};
  };

  const getSnowDot = (dot: AiChatChristmasTreeSnowDot, index: number): ReactElement => {
    return <span key={index} className={styles.snowDot} style={getSnowDotStyle(dot)} />;
  };

  const getSnowDotStyle = (dot: AiChatChristmasTreeSnowDot): CssVariableStyle => {
    return {
      '--snow-delay': `${dot.delayMs}ms`,
      '--snow-drift': `${dot.driftPx}px`,
      '--snow-fall': `${dot.fallPx}px`,
      '--snow-fall-time': `${dot.fallSeconds}s`,
      '--snow-size': `${dot.sizePx}px`,
      left: `${dot.leftPercent}%`,
      top: `${dot.topPercent}%`,
    };
  };

  return (
    <ClickableDiv className={getTreeClassName()} tabIndex={-1} enableFocusOutline={false} aria-hidden="true" onClick={onTreeClick}>
      <span key={chaseCount} className={styles.treeBody}>
        <AiChatChristmasAssetImage asset={AiChatChristmasAsset.TREE} className={styles.treeArt} />
        <span className={styles.lights}>
          {AI_CHAT_CHRISTMAS_TREE_BULBS.map(getBulb)}
          <span className={styles.starGlow} />
          {STAR_SPARK_ANGLES_DEG.map(getStarSpark)}
          {AI_CHAT_CHRISTMAS_TREE_SNOW.map(getSnowDot)}
        </span>
      </span>
    </ClickableDiv>
  );
}
